import { Button, DialogContent, DialogTitle } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse, JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";

export type FinishNotification = { kind: "success"; message: string } | { kind: "error"; message: string };

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  contactIds: string[];

  title: string;
  progressTitle: string;
  actionLabel: string;
  cancelLabel: string;
  actionColor: "primary" | "secondary" | "error";
  actionVariant: "contained" | "outlined";

  startJob: (contactIds: string[]) => Promise<ApiResponse<{ asyncOperationId: string }>>;
  buildFinishNotification: (progress: JobProgress) => FinishNotification;
}

const cancelContactsJob = withErrorHandling(adminApi.cancelContactsJob);

const ContactsJobDialog = ({
  open,
  onClose,
  onSubmit,
  contactIds,
  title,
  progressTitle,
  actionLabel,
  cancelLabel,
  actionColor,
  actionVariant,
  startJob,
  buildFinishNotification,
  children,
}: PropsWithChildren<Props>) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [operationId, setOperationId] = useState<string>();

  const handleJobFinished = useCallback(
    (progress: JobProgress) => {
      const notification = buildFinishNotification(progress);
      if (notification.kind === "success") {
        sendNotification(notification.message);
      } else {
        sendNotificationError(notification.message);
      }

      setOperationId(undefined);
      setIsCancelling(false);
      onSubmit();
    },
    [buildFinishNotification, onSubmit, sendNotification, sendNotificationError]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(adminApi.getContactsJobProgress, {
    onJobFinished: handleJobFinished,
    updateIntervalMs: 500,
  });

  const handleSubmit = async () => {
    if (contactIds.length === 0) {
      return;
    }

    setIsStarting(true);
    const [resp, error] = await withErrorHandling(startJob)(contactIds);
    setIsStarting(false);

    if (error) {
      logError(error, `[ContactsJobDialog ${title}] startJob`);
      sendNotificationError(error.message);
      return;
    }

    setOperationId(resp.asyncOperationId);
    startJobProgressCheck(resp.asyncOperationId);
  };

  const handleCancel = async () => {
    if (!operationId) {
      return;
    }

    setIsCancelling(true);
    const [, error] = await cancelContactsJob(operationId);

    if (error) {
      logError(error, `[ContactsJobDialog ${title}] cancel`);
      sendNotificationError(error.message);
      setIsCancelling(false);
    }
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        {jobProgress ? <AsyncJobProgress title={progressTitle} jobProgress={jobProgress} /> : children}
      </DialogContent>

      <DialogButtons onCancel={jobProgress ? undefined : onClose}>
        {jobProgress ? (
          <Button
            variant="outlined"
            color="secondary"
            loading={isCancelling}
            // Disable Cancel until the worker picks up the job — both contact jobs write
            // totalProgressItems as their first action, so totalProgressItems === 0 means
            // the job is still queued, where cancelling can't render the "Cancelled" label.
            disabled={!operationId || jobProgress.totalProgressItems === 0}
            onClick={handleCancel}
          >
            {cancelLabel}
          </Button>
        ) : (
          <Button variant={actionVariant} color={actionColor} loading={isStarting} onClick={handleSubmit}>
            {actionLabel}
          </Button>
        )}
      </DialogButtons>
    </SmallDialog>
  );
};

export default ContactsJobDialog;
